"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";

import { catalogRefreshEvent, catalogVisibleRefreshSeconds } from "@/app/_lib/catalog-cache-policy";

function hasLiveCatalogData(pathname: string): boolean {
  return pathname === "/" ||
    pathname === "/catalogo" ||
    pathname === "/categorias" ||
    pathname.startsWith("/categorias/") ||
    pathname.startsWith("/productos/") ||
    pathname === "/ubicacion";
}

// Next preserves history entries independently of staleTimes. Refresh on restore.
export function CatalogRouteFreshness() {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    let pendingRefresh: number | undefined;
    let wasHidden = document.visibilityState === "hidden";
    const refreshCatalog = () => {
      router.refresh();
      window.dispatchEvent(new Event(catalogRefreshEvent));
    };
    const refreshVisibleCatalog = () => {
      if (hasLiveCatalogData(pathname) && document.visibilityState !== "hidden") {
        refreshCatalog();
      }
    };
    const onPopState = () => {
      window.clearTimeout(pendingRefresh);
      pendingRefresh = window.setTimeout(() => {
        if (hasLiveCatalogData(window.location.pathname)) {
          refreshCatalog();
        }
      }, 0);
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && hasLiveCatalogData(window.location.pathname)) {
        refreshVisibleCatalog();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        wasHidden = true;
      } else if (wasHidden) {
        wasHidden = false;
        refreshVisibleCatalog();
      }
    };

    const interval = window.setInterval(
      refreshVisibleCatalog,
      catalogVisibleRefreshSeconds * 1_000,
    );
    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(pendingRefresh);
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pathname, router]);

  return null;
}
